
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Review } from '@/lib/types';
import { useToast } from './use-toast';

export const useCompanyReviewsAdmin = () => {
  const { toast } = useToast();
  const [companyReviews, setCompanyReviews] = useState<Review[]>([]);
  const [isLoadingReviews, setIsLoadingReviews] = useState(false);
  
  const loadReviewsForCompany = async (companyName: string) => {
    setIsLoadingReviews(true);
    
    try {
      // Get reviews from Supabase
      const { data: supabaseReviews, error } = await supabase
        .from('reviews')
        .select('*')
        .eq('company', companyName);
        
      if (error) throw error;
      
      // Format Supabase reviews to match our app's Review type
      const formattedSupabaseReviews: Review[] = supabaseReviews.map(review => ({
        id: review.id,
        company: review.company,
        jobTitle: review.job_title,
        reasonForExit: review.reason_for_exit,
        location: review.location,
        exitDate: review.exit_date ? new Date(review.exit_date) : undefined,
        fairWorkInvolved: review.fair_work_involved,
        positiveExperience: review.positive_experience,
        createdAt: new Date(review.created_at).getTime(),
        jobCategory: review.job_category || '',
        isHidden: review.is_hidden
      }));
      
      // Sort reviews by creation date (newest first)
      const allReviews = formattedSupabaseReviews.sort((a, b) => b.createdAt - a.createdAt);
      
      setCompanyReviews(allReviews);
      return allReviews;
    } catch (error) {
      console.error("Error loading reviews:", error);
      toast({
        title: "Failed to load reviews",
        description: "There was an error loading reviews for this company.",
        variant: "destructive"
      });
      return [];
    } finally {
      setIsLoadingReviews(false);
    }
  };

  return {
    companyReviews,
    setCompanyReviews,
    isLoadingReviews,
    loadReviewsForCompany
  };
};
